/**
 * Primary-nav MORE menu — TASK 7.2 Steps 1, 2.
 *
 * Extracted verbatim from the mock's `_openMore()` (L1009-1016): an action
 * sheet titled "More" holding Playlists / Stats / Downloads / Queue /
 * Settings, plus an Admin row appended only when the viewer is an admin.
 * ui-contract §10.1 records the same six rows and the sheet's
 * `role="dialog" aria-label="More"` shape.
 *
 * ── THE ADMIN ROW IS AN AFFORDANCE, NOT AUTHORIZATION ─────────────────────
 * Hiding the row for members is a UI nicety only. Authorization for every
 * admin capability is enforced SERVER-SIDE by `requireAdmin` in
 * `apps/api/src/routes/admin-*.ts`; typing `#/admin/users` by hand, or
 * flipping this flag in devtools, gets a member exactly nothing. The client
 * additionally bounces `/admin*` to `/not-authorized` (router `adminOnly`),
 * which is also cosmetic. Never treat either as a control.
 *
 * The mock's sheet handlers all did `this._closeSheet(); this._go('#/x');`
 * (L1010-1015). Order matters here for a different reason than it did there:
 * closing consumes the overlay's sentinel history entry asynchronously, so the
 * navigation is handed to `closeTop({ then })` and runs once the traversal
 * lands — otherwise the `location.hash` write would race the `history.back()`
 * and could be undone by it. See OverlayStack's header.
 *
 * @module
 */

import { navigate } from './router.js';
import { ActionSheet } from './overlays.js';

/**
 * @typedef {object} MoreMenuEntry
 * @property {string} icon
 * @property {string} label
 * @property {string} href
 * @property {string} [color]
 * @property {boolean} [adminOnly]
 */

/** mock L1010-1015 — the five member rows, then the admin row (L1015). */
export const MORE_MENU_ENTRIES = Object.freeze([
  { icon: '♫', label: 'Playlists', href: '#/playlists' },
  { icon: '★', label: 'Stats', href: '#/stats' },
  { icon: '↓', label: 'Downloads', href: '#/downloads' },
  { icon: '☰', label: 'Queue', href: '#/queue' },
  { icon: '⚙', label: 'Settings', href: '#/settings' },
  {
    icon: '⚡',
    label: 'Admin',
    href: '#/admin/users',
    color: 'var(--sm-color-pink)',
    adminOnly: true,
  },
]);

/**
 * The entries a viewer with `role` sees.
 *
 * @param {'admin'|'member'|null} role
 * @returns {MoreMenuEntry[]}
 */
export function moreMenuEntriesFor(role) {
  return MORE_MENU_ENTRIES.filter((entry) => entry.adminOnly !== true || role === 'admin');
}

/**
 * Opens the MORE menu on the given overlay stack.
 *
 * @param {{
 *   stack: import('./OverlayStack.js').OverlayStackHandle,
 *   role: 'admin'|'member'|null,
 *   go?: (href: string) => void,
 * }} options
 * @returns {import('./OverlayStack.js').OverlayEntry}
 */
export function openMoreMenu(options) {
  const { stack, role, go = navigate } = options;

  const descriptor = ActionSheet({
    title: 'More',
    items: moreMenuEntriesFor(role).map((entry) => ({
      icon: entry.icon,
      label: entry.label,
      ...(entry.color === undefined ? {} : { color: entry.color }),
      onSelect: () => {
        // close FIRST (mock L1010), navigate once the history traversal lands
        stack.closeTop({ then: () => go(entry.href) });
      },
    })),
    onDismissRequest: () => stack.closeTop(),
  });

  return stack.open(descriptor);
}
